import React from 'react'
import { css } from '@emotion/css'
import { FormattedMessage } from 'react-intl'
import { IconWarning } from '@contrast-security-inc/design-system-foundations'
import { Tooltip } from '@contrast-security-inc/design-system-popovers'
import { LibraryPolicies } from '../../helpers/ui-interfaces'
import { LibraryGridPanelID } from '../../helpers/drilldown-ui-interfaces'

const iconSize = 15

const iconContainerCss = (disableLeftMargin: boolean) => css`
  flex-grow: 0;
  ${!disableLeftMargin && 'margin-left: 4px;'}
`

const warningIconCss = css`
  cursor: pointer;
`

const bulletPoint = css`
  margin-left: 5%;
  display: list-item;
`

export const hasInvalidVersion = ({
  invalidVersion,
  versionRestriction
}: {
  readonly invalidVersion: LibraryPolicies['invalidVersion']
  readonly versionRestriction?: LibraryPolicies['versionRestriction']
}) => !!invalidVersion && typeof versionRestriction !== 'undefined'

export const hasRestriction = ({
  restrictedByOrg,
  invalidVersion,
  versionRestriction,
  restrictedLicense
}: LibraryPolicies) => {
  return (
    restrictedByOrg ||
    hasInvalidVersion({ invalidVersion, versionRestriction }) ||
    !!restrictedLicense
  )
}

interface Props {
  disableLeftMargin?: boolean
  policies: LibraryPolicies
  applicationId?: string
  updateActiveRowID?: (newRowID: string) => void
  libraryId?: string
  setSelectedPanelTabId?: React.Dispatch<
    React.SetStateAction<LibraryGridPanelID>
  >
}

export function RestrictionIcon({
  disableLeftMargin = false,
  policies,
  applicationId,
  libraryId,
  updateActiveRowID,
  setSelectedPanelTabId
}: Props) {
  const {
    restrictedByOrg,
    invalidVersion,
    versionRestriction,
    restrictedLicense
  }: LibraryPolicies = policies

  const openOverviewPanel = () => {
    if (libraryId && setSelectedPanelTabId && updateActiveRowID) {
      setSelectedPanelTabId(LibraryGridPanelID.Overview)
      updateActiveRowID(libraryId)
    }
  }

  if (
    hasRestriction({
      restrictedByOrg,
      invalidVersion,
      versionRestriction,
      restrictedLicense
    })
  ) {
    return (
      <div className={iconContainerCss(disableLeftMargin)}>
        <Tooltip
          body={
            <div>
              <div data-testid="tooltip-title" className="tooltip-title">
                <FormattedMessage id="title_attention" />
              </div>
              <div data-testid="general-violation-message">
                <FormattedMessage id="detail_libraryPolicy_violation" />
              </div>
              {!applicationId && (
                <>
                  {restrictedByOrg && <RestrictedByOrgWarning />}
                  {hasInvalidVersion({
                    invalidVersion,
                    versionRestriction
                  }) && (
                    <InvalidVersionWarning
                      versionRestriction={versionRestriction as number}
                    />
                  )}
                  {restrictedLicense && <LicenseRestrictionWarning />}
                </>
              )}
            </div>
          }
        >
          <IconWarning
            testId="restriction-icon"
            width={iconSize}
            height={iconSize}
            className={warningIconCss}
            color="red50"
            onClick={openOverviewPanel}
          />
        </Tooltip>
      </div>
    )
  }
  return <React.Fragment />
}

function RestrictedByOrgWarning() {
  return (
    <div className={bulletPoint} data-testid="restricted-by-org-message">
      <FormattedMessage id="detail_libraryRestricted_policyViolation" />
    </div>
  )
}

function LicenseRestrictionWarning() {
  return (
    <div className={bulletPoint} data-testid="restricted-by-license-message">
      <FormattedMessage id="detail_libraryRestricted_licensePolicyViolation" />
    </div>
  )
}

function InvalidVersionWarning({
  versionRestriction
}: {
  readonly versionRestriction: number
}) {
  return (
    <div className={bulletPoint} data-testid="invalid-version-warning">
      {versionRestriction === 0 && (
        <FormattedMessage id="detail_libraryVersion_policyViolation" />
      )}
      {versionRestriction > 0 && (
        <FormattedMessage
          id="pluralCount_libraryVersion_policyViolationRestriction"
          values={{ count: versionRestriction }}
        />
      )}
    </div>
  )
}
